import { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const MIN_TIME_ON_PAGE_MS = 20000;

export default function ExitIntentModal() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const { t } = useLanguage();
  const arrivalTime = useRef(Date.now());

  useEffect(() => {
    const hasShownBefore = sessionStorage.getItem('exitIntentShown');
    if (hasShownBefore) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown) {
        const timeOnPage = Date.now() - arrivalTime.current;
        if (timeOnPage < MIN_TIME_ON_PAGE_MS) return;
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShown]);

  const handleClose = () => setIsVisible(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setIsVisible(false), 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 left-4 right-4 sm:left-6 sm:right-auto z-[100] animate-fade-in w-auto sm:w-[min(24rem,calc(100vw-3rem))]">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />

        <div className="p-6">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label={t('sticky.close')}
          >
            <X className="w-4 h-4" />
          </button>

          {!submitted ? (
            <>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 pr-6">
                {t('exit.title')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                {t('exit.desc')}
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">
                  {t('exit.offer.title')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('exit.offer.desc')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <label htmlFor="modal-name" className="sr-only">{t('exit.form.name')}</label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:border-blue-600 focus:outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400"
                  placeholder={t('exit.form.placeholder.name')}
                />
                <label htmlFor="modal-email" className="sr-only">{t('exit.form.email')}</label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:border-blue-600 focus:outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400"
                  placeholder={t('exit.form.placeholder.email')}
                />

                <button
                  type="submit"
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 group shadow-md"
                >
                  {t('exit.submit')}
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>

              <button
                onClick={handleClose}
                className="w-full mt-3 text-center text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('exit.dismiss')}
              </button>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                {t('exit.success.title')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('exit.success.desc')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
